

import { apiHandler } from './apihandler';

// apiHandler resolves with the raw axios error on a non-2xx response, so the
// backend's reason lives at res.response.data - surface it instead of a
// generic message so a failed upload says why it failed.
const uploadStepError = (res, step) => {
  const status = res?.response?.status;
  const body = res?.response?.data;
  const reason =
    body?.data?.message ||
    body?.response?.message ||
    body?.message ||
    (typeof body === 'string' && body) ||
    res?.message;
  console.error(`Upload ${step} failed`, status, body || res);
  return new Error(`Failed to ${step}${status ? ` (${status})` : ''}${reason ? `: ${reason}` : ''}`);
};

// Uploads a file (image or video) to S3 using backend multipart presigned URL flow
// selectFile: File object, fileKey: prefix string, fileType: MIME type
// headers: optional auth headers override (needed when the caller isn't authenticated
// via localStorage yet, e.g. mid-onboarding where the token is held in memory only)
const handleImageUpload = async (selectFile, fileKey, fileType, headers = {}) => {
  if (!selectFile) throw new Error('No file provided');
  const partSize = 5 * 1024 * 1024; // 5MB
  const totalSize = selectFile.size;
  const safeFileName = selectFile.name.replace(/[^a-zA-Z0-9._-]/g, '_');
  // Ensure fileKey ends with / if needed, and never encode / as %2F
  let cleanFileKey = fileKey;
  if (cleanFileKey && !cleanFileKey.endsWith('/')) cleanFileKey += '/';
  // Remove any accidental encoding of /
  cleanFileKey = cleanFileKey.replace(/%2F/g, '/');
  const fileName = cleanFileKey + `${Date.now()}` + safeFileName;

  // 1) Create multipart upload (get uploadId)
  const createRes = await apiHandler(
    'users/v1/upload-multipart/create',
    'POST',
    { fileName, contentType: fileType },
    headers
  );
  if (!createRes || !createRes.data) throw uploadStepError(createRes, 'create multipart upload');
  const createJson = createRes.data;
  const uploadId = createJson.uploadId;
  const key = createJson.key || fileName;
  if (!uploadId || !key) throw new Error('No uploadId or key returned');

  // 2) Get presigned URLs for all parts
  const numParts = Math.ceil(totalSize / partSize);
  const partsArray = Array.from({ length: numParts }, (_, i) => i + 1);
  const presignRes = await apiHandler(
    'users/v1/upload-multipart/presign',
    'POST',
    { key, uploadId, parts: partsArray, contentType: fileType },
    headers
  );
  if (!presignRes || !presignRes.data) throw uploadStepError(presignRes, 'get presigned URLs');
  let presigns = presignRes.data.presigns;
  if (!Array.isArray(presigns)) throw new Error('No presigns array returned');
  presigns = presigns.sort((a, b) => a.partNumber - b.partNumber);

  // 3) Upload parts (parallelized)
  const uploadedParts = [];
  let uploadedBytes = 0;
  const CONCURRENT = 4;
  let index = 0;

  async function worker() {
    while (index < presigns.length) {
      const i = index++;
      const { partNumber, url } = presigns[i];
      const start = (partNumber - 1) * partSize;
      const end = Math.min(start + partSize, totalSize);
      const chunk = selectFile.slice(start, end);
      const res = await fetch(url, {
        method: 'PUT',
        headers: { 'Content-Type': fileType },
        body: chunk
      });
      if (!res.ok) throw new Error(`Part ${partNumber} upload failed`);
      const etag = res.headers.get('ETag');
      uploadedParts.push({ ETag: etag ? etag.replace(/"/g, '') : '', PartNumber: partNumber });
      uploadedBytes += chunk.size;
      // Optionally: report progress here
    }
  }
  await Promise.all(Array.from({ length: CONCURRENT }, () => worker()));

  // 4) Complete upload
  const completeRes = await apiHandler(
    'users/v1/upload-multipart/complete',
    'POST',
    { key, uploadId, parts: uploadedParts },
    headers
  );
  if (!completeRes || !completeRes.data) throw uploadStepError(completeRes, 'complete upload');
  const completeJson = completeRes.data;
  console.log('Upload complete:', completeJson);
  if (!completeJson.url && !completeJson.location) throw new Error('No file URL returned from complete');
  return completeJson.url || completeJson.location;
};

export { handleImageUpload };